/* eslint-disable max-lines */
import { Tooltip } from '@heroui/react';
import {
  Check, Edit2, Trash2, X,
} from 'lucide-react';
import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ModalConfirmation } from '@/components';
import { ds } from '@/config/colors';
import { useOrganizationState, useUserState } from '@/state';
import {
  Permission,
  Role,
  roleLabels,
  StaffServicePermission,
} from '@/views/Settings/queries';
import { getStaffSectionLabel } from '@/views/Team/components/staffSectionsCatalog';
import { readStaffPermissions } from '@/views/Team/utils/staffPermissions';

/* ============================================================
   Kompaktowa tabela członków zespołu — w stylu reszty aplikacji
   (taki sam pattern jak SpecialOffer / Channels / Hotel widoki).
   ============================================================ */

const ListTableWrap = styled.div`
  /* Tabela jak w hotelspot-audit: bez ramki, tła i bocznych wcięć (DS). */
  width: 100%;
  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  /* Nagłówek wyraźny (13 px, medium, czarny tekst), linie nad i pod, bez szarego tła. */
  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.primary};
    background: transparent;
    border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? theme.colors.border.primary : ds.borderStrong)};
    border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? theme.colors.border.primary : ds.borderStrong)};
  }
  thead th:first-child,
  tbody td:first-child { padding-left: 0; }
  thead th:last-child,
  tbody td:last-child { padding-right: 0; }
  thead th.center { text-align: left; }
  thead th.right { text-align: left; }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    transition: background 0.15s;
  }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(17, 24, 39, 0.02)')};
  }

  tbody td {
    padding: 8px 16px;
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  tbody td.center { text-align: center; }
  tbody td.right { text-align: right; }
  tbody td.muted { color: ${({ theme }) => theme.colors.text.secondary}; }
`;

const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/* Nazwa + ołówek: ołówek pojawia się po najechaniu na wiersz (klasa group na <tr>). */
const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.35;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 300;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f3f4f6')};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CompactIconButton = styled.button<{ $variant?: 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#dcdfe4')};
  color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#dc2626';
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) => {
    if ($variant === 'danger') return theme.mode === 'dark' ? 'rgba(239,68,68,0.12)' : '#fee2e2';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb';
  }};
    border-color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#fecaca';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg { width: 14px; height: 14px; }
`;

const CompactActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const EmptyTableContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 6px;
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 380px;
  line-height: 1.5;
`;

const LoadingSkeleton = styled.div`
  height: 56px;
  width: 100%;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0.07) 50%,
    rgba(0, 0, 0, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: skeletonShimmer 1.6s ease-in-out infinite;

  @keyframes skeletonShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

interface TeamMember {
  id: string;
  email: string;
  role?: string | string[];
  permissions?: Permission[];
  staffService?: boolean;
  staffServicePermissions?: StaffServicePermission[];
  staffAccess?: Record<string, boolean>;
  staffSections?: Record<string, string[]>;
  staffOnly?: boolean;
  displayName?: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  fcmToken?: string;
  fcmTokens?: string[];
  [key: string]: any;
}

interface TeamMembersSectionProps {
  teamMembers?: TeamMember[];
  isLoading: boolean;
  /**
   * Klik w „edytuj" — formularz jest osobnym WIDOKIEM, więc komponent tylko
   * zgłasza intencję, a nawigację robi strona listy (dawniej: modal tutaj).
   */
  onEditMemberClick?: (memberId: string) => void;
  onDeleteMember?: (memberId: string) => void;
  /**
   * Czy pokazywać akcje na wierszach. Edycja i usuwanie to uprawnienie
   * właściciela/administratora — reszta zespołu widzi listę do odczytu.
   */
  canManage?: boolean;
  isDeleting?: boolean;
}

export const TeamMembersSection: FC<TeamMembersSectionProps> = ({
  teamMembers,
  isLoading,
  onEditMemberClick,
  onDeleteMember,
  canManage = false,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useOrganizationState();
  const { currentUser } = useUserState();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    memberId: string | null;
    memberEmail: string | null;
  }>({
    isOpen: false,
    memberId: null,
    memberEmail: null,
  });

  const prevIsDeletingRef = useRef(isDeleting);

  useEffect(() => {
    if (prevIsDeletingRef.current && !isDeleting) {
      setDeleteConfirmation({
        isOpen: false,
        memberId: null,
        memberEmail: null,
      });
    }
    prevIsDeletingRef.current = isDeleting;
  }, [isDeleting]);

  const handleDeleteClick = (memberId: string, memberEmail: string) => {
    setDeleteConfirmation({
      isOpen: true,
      memberId,
      memberEmail,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.memberId && onDeleteMember) {
      onDeleteMember(deleteConfirmation.memberId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      memberId: null,
      memberEmail: null,
    });
  };

  const isEmpty = !isLoading && (!teamMembers || teamMembers.length === 0);

  return (
    <>
      <SectionHeader>
        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>
          {teamMembers
            ? t('settings.teamMembersCount', '{{count}} użytkowników', { count: teamMembers.length })
            : ''}
        </span>
      </SectionHeader>

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
      ) : (
        <ListTableWrap>
          <ListTable>
            <thead>
              <tr>
                <th>{t('settings.teamTable.user', 'Użytkownik')}</th>
                <th style={{ width: 140 }}>{t('settings.teamTable.role', 'Rola')}</th>
                <th style={{ width: 160 }}>{t('settings.teamTable.staffApp', 'Aplikacja Staff')}</th>
                <th className="right" style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={4} style={{ padding: 0 }}>
                    <EmptyTableContent>
                      <EmptyTitle>
                        {t('settings.noTeamMembers', 'Brak użytkowników')}
                      </EmptyTitle>
                      <EmptyDescription>
                        {t(
                          'settings.noTeamMembersDescription',
                          'Dodaj pierwszego członka zespołu. Będzie mógł zarządzać organizacją i korzystać z aplikacji Staff.',
                        )}
                      </EmptyDescription>
                    </EmptyTableContent>
                  </td>
                </tr>
              ) : (
                (teamMembers || []).map((member) => {
                  // Dostęp i sekcje Staff są per hotel (#83); stare konta czytane ze staffServicePermissions.
                  const memberStaff = readStaffPermissions(member, organizationId ?? '');
                  const userStaffService = memberStaff.staffAccess;
                  const userName = member.name || '';
                  const userSurname = member.surname || '';

                  const displayName = member.displayName
                    || (userName || userSurname ? `${userName} ${userSurname}`.trim() : member.email);

                  const canEdit = canManage && !!onEditMemberClick;

                  return (
                    <tr
                      key={member.id}
                      className={canEdit ? 'group cursor-pointer' : undefined}
                      onClick={canEdit ? () => onEditMemberClick?.(member.id) : undefined}
                    >
                      <td>
                        <UserCell>
                          <NameLine>
                            <UserName className={canEdit ? 'group-hover:underline' : undefined}>{displayName}</UserName>
                            {canEdit && (
                              <CompactIconButton
                                type="button"
                                $variant="secondary"
                                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity shrink-0"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onEditMemberClick?.(member.id);
                                }}
                                aria-label={String(t('common.edit', 'Edytuj'))}
                                title={String(t('common.edit', 'Edytuj'))}
                              >
                                <Edit2 />
                              </CompactIconButton>
                            )}
                          </NameLine>
                          {displayName !== member.email && (
                            <UserEmail>{member.email}</UserEmail>
                          )}
                        </UserCell>
                      </td>
                      <td>
                        <RoleBadge>
                          {member.role
                            ? Array.isArray(member.role)
                              ? member.role.map((r) => roleLabels[r as Role] || r).join(', ')
                              : roleLabels[member.role as Role] || member.role
                            : 'User'}
                        </RoleBadge>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            userStaffService && memberStaff.staffSections.length > 0
                              ? memberStaff.staffSections.map((key) => getStaffSectionLabel(key, t)).join(', ')
                              : t('settings.staffServiceUnavailable', 'Aplikacja Staff niedostępna')
                          }
                          placement="top"
                          radius="sm"
                          size="sm"
                          delay={300}
                          classNames={{
                            base: 'pointer-events-auto',
                            content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                          }}
                        >
                          {/* Dostęp do Staffa: tick w zielonym kółku jak w innych tabelach, brak: szary X. */}
                          {userStaffService ? (
                            <span
                              role="img"
                              aria-label={String(t('settings.staffServiceActive', 'Aktywne'))}
                              className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#16a34a] text-white"
                            >
                              <Check size={14} strokeWidth={3} aria-hidden />
                            </span>
                          ) : (
                            <span
                              role="img"
                              aria-label={String(t('settings.staffServiceInactive', 'Nieaktywne'))}
                              className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#f1f3f5] text-text-secondary dark:bg-white/10"
                            >
                              <X size={14} strokeWidth={3} aria-hidden />
                            </span>
                          )}
                        </Tooltip>
                      </td>
                      <td className="right" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        {canManage && member.id !== currentUser?.uid && (
                          <CompactActions>
                            <Tooltip
                              content={t('common.delete', 'Usuń')}
                              placement="top"
                              radius="sm"
                              size="sm"
                              delay={300}
                              classNames={{
                                base: 'pointer-events-auto',
                                content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                              }}
                            >
                              <span>
                                <CompactIconButton
                                  type="button"
                                  $variant="danger"
                                  onClick={() => handleDeleteClick(member.id, member.email)}
                                  disabled={isDeleting}
                                  aria-label={String(t('common.delete', 'Usuń'))}
                                >
                                  <Trash2 />
                                </CompactIconButton>
                              </span>
                            </Tooltip>
                          </CompactActions>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ListTable>
        </ListTableWrap>
      )}

      <ModalConfirmation
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        onConfirm={() => handleConfirmDelete()}
        itemId={deleteConfirmation.memberId}
        title={t('settings.deleteTeamMember', 'Usuń użytkownika')}
        description={t(
          'settings.deleteTeamMemberConfirmation',
          `Czy na pewno chcesz usunąć użytkownika ${deleteConfirmation.memberEmail}?`,
          { email: deleteConfirmation.memberEmail },
        )}
        buttonConfirm={t('common.delete', 'Usuń')}
        isLoading={isDeleting}
      />
    </>
  );
};
